import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { InfoSheetPanel, H3, Subheading, Row, Button, Separator, useOverlay, usePageWidth } from '@fs/zion-ui'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  getNestedOrFloatingElementById,
  getNextArkId,
  getPersonName,
  getPersonRelationships,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext } from '../../providers/FamilyReviewContext'

import Tip from '../../../../components/tip/Tip'
import useFamilyReviewRelationshipOptions from '../../hooks/useFamilyReviewRelationshipOptions'
import { saveNewPerson, saveExistingPerson } from '../../utils/savePersonFunctions'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import { getPrimaryOrFirstPerson, sortFunction } from '../../utils/utils'
import { showEssentialInfoHighlights } from '../../utils/highlightUtils'

import CrossRecordWarningOverlay from '../../../indexing/components/editPanels/CrossRecordWarningOverlay'
import CreateNewPersonFormPage1 from '../createNewPerson/CreateNewPersonFormPage1'
import CreateNewPersonFormPage2 from '../createNewPerson/CreateNewPersonFormPage2'
import { getNextUuid } from '../../../../../shared/utils/utils'

export default function AddNewPerson({ currentTask, addNewPersonOverlay, editHighlightsOverlay, setSaveUpdateCalled }) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const primaryPerson = getPrimaryOrFirstPerson(currentTask)
  const primaryPersonName = getPersonName({ person: primaryPerson, justOneName: true })
  const crossRecordOverlay = useOverlay()
  const createNewPersonFormPage1Overlay = useOverlay()
  const createNewPersonFormPage2Overlay = useOverlay()

  const autocompleteSuggestions = useFamilyReviewRelationshipOptions({
    images: zionSlsState.images,
    mainPartitionKey: currentTask.partitionKey,
    splitScreenPartitionKey: state.splitScreenPartitionKey,
    primaryPersonId: primaryPerson?.id,
    currentRecordId: currentTask?.id,
  })

  const [nameType, setNameType] = useState('BIRTH_NAME')
  const [givenField, setGivenField] = useState('')
  const [surnameField, setSurnameField] = useState('')
  const [givenNameFieldId, setGivenNameFieldId] = useState()
  const [surnameFieldId, setSurnameFieldId] = useState()
  const [crossRecordPerson, setCrossRecordPerson] = useState()
  const [crossRecordRelationships, setCrossRecordRelationships] = useState([])
  const [personId, setPersonId] = useState('')
  const [inputText, setInputText] = useState('')

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      selectedId: null,
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { selectedId } = values
      if (!selectedId)
        errors.selectedId = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      return { values, errors }
    },
  })

  const [selectedId] = useWatch({
    name: ['selectedId'],
    control: methods.control,
  })

  useEffect(() => {
    let isMounted = true

    const resetPersonIdSafe = async () => {
      const id = await getNextArkId('person')
      if (isMounted) {
        setPersonId(id)
        dispatch({
          type: 'SET_SELECTED_PERSON_ID',
          value: id,
        })
      }
    }

    if (addNewPersonOverlay.isOpen) {
      methods.reset({ selectedId: null }) // Reset this whenever overlay opens
      setInputText('')
      resetPersonIdSafe()
    }

    return () => {
      isMounted = false // Cleanup when component unmounts
    }
  }, [addNewPersonOverlay.isOpen, methods, dispatch])

  const resetStateAndHighlights = () => {
    if (state.currentStep === stepsMap.STEP_ONE) {
      const primaryPersonId = getPrimaryOrFirstPerson(currentTask)
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [primaryPersonId],
        isSmallScreen,
      })
      // Set selected person id
      dispatch({ type: 'SET_SELECTED_PERSON_ID', value: primaryPersonId })
    } else if (state.currentStep === stepsMap.STEP_TWO) {
      const nextRelationshipCard = state.stepTwo.relationshipCards.find((cardState) => !cardState.hasBeenReviewed)
      if (nextRelationshipCard) {
        dispatch({
          type: 'SET_SELECTED_PERSON_ID',
          value: nextRelationshipCard.personId,
        })
        showEssentialInfoHighlights({
          h,
          mainPartitionKey: currentTask.partitionKey,
          splitScreenPartitionKey: state.splitScreenPartitionKey,
          personIds: [nextRelationshipCard.personId],
          isSmallScreen,
        })
      }
    }
  }

  const handleClosingAddNewPerson = () => {
    addNewPersonOverlay.close()
    resetStateAndHighlights()
  }

  const backButtonClicked = () => {
    handleClosingAddNewPerson()
  }

  const saveButtonClicked = async () => {
    const selectedPerson = getNestedOrFloatingElementById(zionSlsState.images, selectedId.key)
    await saveExistingPerson({
      partitionKey: zionSlsState.currentImageArk,
      selectedPerson,
      primaryPerson,
      currentStep: state.currentStep,
      images: zionSlsState.images,
      userId: state.userId,
      zionSLSState: zionSlsState,
      zionSlsDispatch,
      saveChangesFlag,
    })
    setSaveUpdateCalled(true)
    handleClosingAddNewPerson()
  }

  const hasSelectedHighlights = useCallback(
    (fieldId) => {
      return [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights].find(
        (highlight) => highlight.fieldId === fieldId
      )
    },
    [h]
  )

  const handleSubmitCreateForm1 = (values, givenNameFieldIdTemp, surnameFieldIdTemp) => {
    setNameType(values.nameType)
    setGivenField(values.givenField)
    setSurnameField(values.surnameField)
    setGivenNameFieldId(givenNameFieldIdTemp)
    setSurnameFieldId(surnameFieldIdTemp)
    const addedSexId = getNextUuid()
    dispatch({ type: 'SET_STATE', value: { addedSexId } })
    createNewPersonFormPage2Overlay?.handleClick()
  }

  const handleSubmitCreateForm2 = async (values, sexFieldId) => {
    await saveNewPerson({
      partitionKey: zionSlsState.currentImageArk,
      addNewPersonData: {
        nameType,
        givenField,
        surnameField,
        sexField: values.sexField,
        relationshipField: values.relationshipField,
        givenNameFieldId,
        surnameFieldId,
        sexFieldId,
      },
      personId,
      userId: state.userId,
      recordId: primaryPerson.recordId,
      setAsPrimary: state.currentStep === stepsMap.STEP_ONE,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      relatedPersonId: primaryPerson?.id,
      zionSLSState: zionSlsState,
      zionSlsDispatch,
      saveChangesFlag,
      currentTask,
    })
    setSaveUpdateCalled(true)
    createNewPersonFormPage2Overlay.close()
    createNewPersonFormPage1Overlay.close()
    addNewPersonOverlay.close()
    resetStateAndHighlights()
  }

  const userClosedPanel = () => {
    handleClosingAddNewPerson()
  }

  const handleOnCancelCrossRecordWarning = () => {
    methods.reset({ selectedId: null })
    h.clearDisplay({ all: true })
  }
  return (
    <>
      <InfoSheetPanel
        {...{ ...addNewPersonOverlay, close: userClosedPanel }}
        headingColor="green"
        title={
          state.currentStep === stepsMap.STEP_ONE
            ? t('family-review.step1.heading', 'Identify Primary Person')
            : t('family-review.step3.heading', 'Family Relationships')
        }
        footer={
          <Row alignX="end">
            <Button onClick={backButtonClicked} data-testid="add-person-back-button" linkName="Add Person Go Back">
              {t('navigation.back-button', 'Back')}
            </Button>
            <Button
              emphasis="high"
              linkName="Add Person Save"
              data-testid="add-person-save-button"
              type="submit"
              form="add-person"
            >
              {t('navigation.save-button', 'Save')}
            </Button>
          </Row>
        }
      >
        {addNewPersonOverlay.isOpen && (
          <>
            <H3 size="H5">
              {t('family-review.addPerson.heading', 'Add a Person')}
              <Subheading size="sm">
                {t(
                  'family-review.addPerson.content',
                  'Fill out the following information to add a person to the record.'
                )}
              </Subheading>
            </H3>
            <Separator size="sm" />
            <ZionForm methods={methods} onSubmit={saveButtonClicked} id="add-person">
              <FormAutosuggest
                label={t('family-review.addPerson.select-person', 'Select Person')}
                placeholder={t('family-review.addPerson.select-person', 'Select Person')}
                suggestions={autocompleteSuggestions}
                sortFunction={sortFunction}
                highlightMatchingText
                name="selectedId"
                inputText={inputText}
                onInputTextChange={setInputText}
                lastResort={{
                  key: 'LAST-RESORT-KEY',
                  primaryText: t('family-review.addPerson.name-not-on-list', 'Name Not In This List'),
                  onClick: () => {
                    createNewPersonFormPage1Overlay.handleClick()
                    methods.reset({ selectedId: null })
                    setInputText('')
                  },
                }}
                showLastResortOnFocus
                onSuggestionSelected={(suggestion) => {
                  if (!createNewPersonFormPage1Overlay.isOpen) {
                    const selectedPerson = getNestedOrFloatingElementById(zionSlsState.images, suggestion.key)
                    dispatch({ type: 'SET_SELECTED_PERSON_ID', value: suggestion?.key })
                    if (selectedPerson.recordId !== primaryPerson.recordId) {
                      const relationships = getPersonRelationships(selectedPerson, zionSlsState.images)
                      if (relationships.length > 0) {
                        setCrossRecordPerson(selectedPerson)
                        setCrossRecordRelationships(relationships)
                        crossRecordOverlay.open()
                      }
                    }
                    showEssentialInfoHighlights({
                      h,
                      mainPartitionKey: currentTask.partitionKey,
                      splitScreenPartitionKey: state.splitScreenPartitionKey,
                      personIds: [suggestion.key],
                      isSmallScreen,
                    })
                  }
                }}
              />
            </ZionForm>
            <Tip
              primaryText={t('family-review.addPerson.tip-primary-text', "Don't see the name you are looking for?")}
              listPrimaryText={
                <Trans i18nKey="family-review.addPerson.list-primary-text">
                  <span>
                    Looks like our computer left someone out! <strong>Select</strong> the &quot;Name Not In This
                    List&quot; option in the menu add a new person.
                  </span>
                </Trans>
              }
            />
            {crossRecordPerson && (
              <CrossRecordWarningOverlay
                overlay={crossRecordOverlay}
                person={crossRecordPerson}
                existingRelationships={crossRecordRelationships}
                genericVersion
                onCancel={handleOnCancelCrossRecordWarning}
              />
            )}
          </>
        )}
      </InfoSheetPanel>
      <CreateNewPersonFormPage1
        createNewPersonFormPage1Overlay={createNewPersonFormPage1Overlay}
        currentTask={currentTask}
        handleSubmit={handleSubmitCreateForm1}
        hasSelectedHighlights={hasSelectedHighlights}
        editHighlightsOverlay={editHighlightsOverlay}
      />
      <CreateNewPersonFormPage2
        createNewPersonFormPage2Overlay={createNewPersonFormPage2Overlay}
        handleSubmit={handleSubmitCreateForm2}
        givenField={givenField}
        surnameField={surnameField}
        primaryPersonName={primaryPersonName}
        editHighlightsOverlay={editHighlightsOverlay}
        currentTask={currentTask}
      />
    </>
  )
}
